import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, ArrowLeft, Plus, Trash2, AlertTriangle, TrendingUp, TrendingDown, 
  Target, PieChart, BarChart3, Home, Utensils, Car, Gamepad2, Heart, 
  BookOpen, MoreHorizontal, Tag, Loader2, Crown, Settings, Lock, Sparkles,
  Pin, PinOff, Edit2, Check, Circle, Cloud, Trophy, Wallet, Flag, Flame
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import QuickNav from '@/components/QuickNav';
import PushNotifications from '@/components/PushNotifications';
import LoginPromptModal from '@/components/LoginPromptModal';
import { useLocalExpenses, DEFAULT_CATEGORIES } from '@/hooks/useLocalExpenses';
import { useGamification } from '@/hooks/useGamification';
import CelebrationModal from '@/components/CelebrationModal';
import { API } from '@/lib/api';
import { trackStartTrial, trackStartOrganizer, trackFirstExpense } from '@/lib/metaPixel';

const iconMap = {
  home: Home,
  utensils: Utensils,
  car: Car,
  gamepad: Gamepad2,
  heart: Heart,
  book: BookOpen,
  'more-horizontal': MoreHorizontal,
  tag: Tag,
};

const ExpensesPage = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accessInfo, setAccessInfo] = useState(null);
  const [categories, setCategories] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showSetGoal, setShowSetGoal] = useState(false);
  const [savingExpense, setSavingExpense] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [debtTotal, setDebtTotal] = useState(0);
  const [debtInicial, setDebtInicial] = useState(0);
  const [showPayoffModal, setShowPayoffModal] = useState(false);
  const [payoffAmount, setPayoffAmount] = useState('');
  const [payingDebt, setPayingDebt] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const [showEditExpense, setShowEditExpense] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [loginPromptVariant, setLoginPromptVariant] = useState('subtle');
  const [incomeList, setIncomeList] = useState([]);
  const [showAddIncome, setShowAddIncome] = useState(false);
  const [newIncome, setNewIncome] = useState({ name: '', amount: '', income_type: 'fixa', date: new Date().toISOString().slice(0, 10) });
  
  // Hook para despesas locais (modo anônimo)
  const localExpensesHook = useLocalExpenses();
  
  // Determinar se está em modo anônimo
  const isAnonymousMode = !user;
  const gamif = useGamification(isAnonymousMode);
  
  const [newExpense, setNewExpense] = useState({
    category_id: '',
    description: '',
    amount: '',
    date: new Date().toISOString().slice(0, 10),
    is_fixed: false,
    recur_type: 'normal',
    installments_total: ''
  });
  
  const [newCategory, setNewCategory] = useState({
    name: '',
    color: '#00FF94',
    icon: 'tag'
  });
  
  const [savingsGoal, setSavingsGoal] = useState('');
  
  // Financial Goals (Metas)
  const [financialGoals, setFinancialGoals] = useState([]);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [showUpdateGoal, setShowUpdateGoal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [newGoal, setNewGoal] = useState({ name: '', target_amount: '', current_amount: '0' });
  const [updateGoalAmount, setUpdateGoalAmount] = useState('');

  useEffect(() => {
    // Se usuário logado, verifica acesso no backend
    // Se não logado, usa modo anônimo com localStorage
    if (user) {
      checkAccessAndLoad();
    } else {
      // Modo anônimo - carrega dados locais
      setCategories(DEFAULT_CATEGORIES);
      setAccessInfo({ has_access: true, is_anonymous: true, is_trial: false });
      setLoading(false);
      
      // Verificar se deve mostrar prompt de login (após 24h)
      if (localExpensesHook.shouldShowLoginPrompt() && localExpensesHook.isReturningUser()) {
        setLoginPromptVariant('strong');
        setShowLoginPrompt(true);
      }
      
      // Atualizar última visita
      localExpensesHook.updateLastVisit();
    }
  }, [user]);

  // Ler dívida (anônimo: localStorage | autenticado: backend) e carregar gamificação
  useEffect(() => {
    const loadDebt = async () => {
      if (isAnonymousMode) {
        try {
          const d = JSON.parse(localStorage.getItem('evite_dividas_debt') || 'null');
          if (d) {
            if (typeof d.total === 'number') setDebtTotal(d.total);
            setDebtInicial(typeof d.inicial === 'number' ? d.inicial : (d.total || 0));
          }
        } catch (e) { /* ignore */ }
      } else {
        try {
          const res = await axios.get(`${API}/debt`, { withCredentials: true });
          let { inicial, atual } = res.data || {};
          // Migrar dívida do wizard (localStorage) caso o backend ainda não tenha
          if ((!inicial || inicial <= 0)) {
            try {
              const local = JSON.parse(localStorage.getItem('evite_dividas_debt') || 'null');
              if (local && typeof local.inicial === 'number' && local.inicial > 0) {
                const setRes = await axios.post(`${API}/debt`, { inicial: local.inicial }, { withCredentials: true });
                inicial = setRes.data.inicial;
                atual = setRes.data.atual;
                // Preservar progresso de abatimento feito no modo anônimo
                const jaPago = local.inicial - (typeof local.total === 'number' ? local.total : local.inicial);
                if (jaPago > 0) {
                  const payRes = await axios.post(`${API}/debt/payoff`, { amount: jaPago }, { withCredentials: true });
                  atual = payRes.data.atual;
                }
                localStorage.removeItem('evite_dividas_debt');
              }
            } catch (e) { /* ignore */ }
          }
          setDebtInicial(inicial || 0);
          setDebtTotal(atual || 0);
        } catch (e) { /* ignore */ }
      }
    };
    loadDebt();
    gamif.loadStatus();
  }, [isAnonymousMode]);

  // Abater dívida (subtrair valor pago do saldo atual)
  const handlePayoffDebt = async () => {
    const amount = parseFloat(payoffAmount);
    if (!amount || amount <= 0) return;
    setPayingDebt(true);
    try {
      const novo = Math.max(debtTotal - amount, 0);
      const acabou = novo <= 0 && debtInicial > 0;
      if (isAnonymousMode) {
        const jaCelebrou = (() => {
          try { return JSON.parse(localStorage.getItem('evite_dividas_debt') || '{}').quitada_celebrou; }
          catch (e) { return false; }
        })();
        localStorage.setItem('evite_dividas_debt', JSON.stringify({
          inicial: debtInicial,
          total: novo,
          quitada_celebrou: acabou ? true : jaCelebrou,
          updated_at: new Date().toISOString(),
        }));
        setDebtTotal(novo);
        if (acabou && !jaCelebrou) setCelebration({ reason: 'debt' });
      } else {
        const res = await axios.post(`${API}/debt/payoff`, { amount }, { withCredentials: true });
        setDebtTotal(res.data.atual);
        if (res.data.celebrate) setCelebration(res.data.celebrate);
      }
      toast.success(`Você abateu ${formatCurrency(amount)} da sua dívida! 💪`);
      setShowPayoffModal(false);
      setPayoffAmount('');
    } catch (e) {
      toast.error('Não foi possível registrar o abatimento. Tente novamente.');
    } finally {
      setPayingDebt(false);
    }
  };

  useEffect(() => {
    if (isAnonymousMode) {
      // Modo anônimo - gera recorrências (fixas/parceladas) automaticamente
      localExpensesHook.ensureRecurringLocal(selectedMonth);
      // Carrega do localStorage
      const localExpenses = localExpensesHook.getExpensesByMonth(selectedMonth);
      setExpenses(localExpenses);
      const localInc = localExpensesHook.getIncomeByMonth(selectedMonth);
      setIncomeList(localInc);
      const localSummary = localExpensesHook.getSummary(selectedMonth);
      
      // Carregar meta do localStorage
      const savedGoal = localStorage.getItem('evite_dividas_savings_goal');
      if (savedGoal) {
        try {
          const goalData = JSON.parse(savedGoal);
          localSummary.savings_goal = goalData.monthly_goal;
          // Calcular savings_status (meta - total gasto)
          localSummary.savings_status = goalData.monthly_goal - localSummary.total;
        } catch (e) {
          console.error('Error parsing savings goal:', e);
        }
      }
      
      setSummary(localSummary);
      loadFinancialGoals();
    } else if (accessInfo?.has_access) {
      // Modo autenticado - gera recorrências (despesas/rendas) antes do resumo
      (async () => {
        await Promise.all([loadExpenses(), loadIncome()]);
        await loadSummary();
      })();
    }
  }, [selectedMonth, accessInfo?.has_access, isAnonymousMode, localExpensesHook.localExpenses, localExpensesHook.localIncome]);

  const checkAccessAndLoad = async () => {
    setLoading(true);
    try {
      const accessRes = await axios.get(`${API}/expenses/access`, { withCredentials: true });
      
      // Explicitly check if has_access is true
      if (accessRes.data && accessRes.data.has_access === true) {
        setAccessInfo(accessRes.data);
        
        // Show welcome message if trial was just activated
        if (accessRes.data.just_activated) {
          toast.success('🎉 Seu teste grátis de 7 dias foi ativado! Aproveite!', {
            duration: 5000
          });
          trackStartTrial({ trial_days: 7 });
          trackStartOrganizer();
        }
        
        await Promise.all([
          loadCategories(),
          loadFinancialGoals(),
          // Only load comparison for premium users (not trial)
          !accessRes.data.is_trial ? loadComparison() : Promise.resolve()
        ]);
        // Generate recurring items (expenses/income) before computing the summary
        await Promise.all([loadExpenses(), loadIncome()]);
        await loadSummary();
      } else {
        // No access - show upgrade screen
        setAccessInfo({ 
          has_access: false, 
          access_type: null,
          trial_expired: accessRes.data?.trial_ends_at ? true : false
        });
      }
    } catch (error) {
      console.error('Error checking access:', error);
      if (error.response?.status === 401) {
        navigate('/');
      } else if (error.response?.status === 403) {
        // Access denied - trial expired or no subscription
        setAccessInfo({ has_access: false, access_type: null, trial_expired: true });
      } else {
        setAccessInfo({ has_access: false });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API}/expenses/categories`, { withCredentials: true });
      setCategories(res.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadExpenses = async () => {
    try {
      const res = await axios.get(`${API}/expenses?month=${selectedMonth}`, { withCredentials: true });
      setExpenses(res.data);
    } catch (error) {
      console.error('Error loading expenses:', error);
    }
  };

  const loadIncome = async () => {
    try {
      const res = await axios.get(`${API}/income?month=${selectedMonth}`, { withCredentials: true });
      setIncomeList(res.data.incomes || []);
    } catch (error) {
      console.error('Error loading income:', error);
    }
  };

  const loadSummary = async () => {
    try {
      const res = await axios.get(`${API}/expenses/summary?month=${selectedMonth}`, { withCredentials: true });
      setSummary(res.data);
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const loadComparison = async () => {
    try {
      const res = await axios.get(`${API}/expenses/comparison`, { withCredentials: true });
      setComparison(res.data);
    } catch (error) {
      console.error('Error loading comparison:', error);
    }
  };

  // Financial Goals
  const loadFinancialGoals = async () => {
    if (isAnonymousMode) {
      const saved = localStorage.getItem('evite_dividas_goals');
      if (saved) {
        try { setFinancialGoals(JSON.parse(saved)); } catch(e) {}
      }
      return;
    }
    try {
      const res = await axios.get(`${API}/expenses/goals`);
      setFinancialGoals(res.data);
    } catch (error) {
      console.error('Error loading goals:', error);
    }
  };

  const handleCreateGoal = async () => {
    if (!newGoal.name.trim() || !newGoal.target_amount || parseFloat(newGoal.target_amount) <= 0) {
      toast.error('Preencha o nome e valor da meta');
      return;
    }
    const goalData = {
      goal_id: `local_goal_${Date.now()}`,
      name: newGoal.name.trim(),
      target_amount: parseFloat(newGoal.target_amount),
      current_amount: parseFloat(newGoal.current_amount) || 0,
      created_at: new Date().toISOString()
    };

    if (isAnonymousMode) {
      const updated = [...financialGoals, goalData];
      setFinancialGoals(updated);
      localStorage.setItem('evite_dividas_goals', JSON.stringify(updated));
      toast.success('Meta criada!');
    } else {
      try {
        const res = await axios.post(`${API}/expenses/goals`, {
          name: goalData.name,
          target_amount: goalData.target_amount,
          current_amount: goalData.current_amount
        });
        setFinancialGoals(prev => [res.data, ...prev]);
        toast.success('Meta criada!');
      } catch (error) {
        toast.error('Erro ao criar meta');
        return;
      }
    }
    setShowAddGoal(false);
    setNewGoal({ name: '', target_amount: '', current_amount: '0' });
  };

  const handleUpdateGoalProgress = async () => {
    if (!editingGoal || !updateGoalAmount) return;
    const newAmount = parseFloat(updateGoalAmount);
    if (isNaN(newAmount) || newAmount < 0) {
      toast.error('Valor inválido');
      return;
    }

    if (isAnonymousMode) {
      const updated = financialGoals.map(g => 
        g.goal_id === editingGoal.goal_id ? { ...g, current_amount: newAmount } : g
      );
      setFinancialGoals(updated);
      localStorage.setItem('evite_dividas_goals', JSON.stringify(updated));
      toast.success('Meta atualizada!');
    } else {
      try {
        await axios.patch(`${API}/expenses/goals/${editingGoal.goal_id}`, {
          current_amount: newAmount
        });
        setFinancialGoals(prev => prev.map(g => 
          g.goal_id === editingGoal.goal_id ? { ...g, current_amount: newAmount } : g
        ));
        toast.success('Meta atualizada!');
      } catch (error) {
        toast.error('Erro ao atualizar meta');
        return;
      }
    }
    setShowUpdateGoal(false);
    setEditingGoal(null);
    setUpdateGoalAmount('');
  };

  const handleDeleteGoal = async (goalId) => {
    if (isAnonymousMode) {
      const updated = financialGoals.filter(g => g.goal_id !== goalId);
      setFinancialGoals(updated);
      localStorage.setItem('evite_dividas_goals', JSON.stringify(updated));
      toast.success('Meta removida');
    } else {
      try {
        await axios.delete(`${API}/expenses/goals/${goalId}`);
        setFinancialGoals(prev => prev.filter(g => g.goal_id !== goalId));
        toast.success('Meta removida');
      } catch (error) {
        toast.error('Erro ao remover meta');
      }
    }
  };

  const getSpendingFeedback = () => {
    if (!summary?.savings_goal || !summary?.total) return null;
    const pct = (summary.total / summary.savings_goal) * 100;
    if (pct <= 60) return { msg: 'Você está dentro do limite. Continue assim!', color: 'text-[#00FF94]', icon: '✓' };
    if (pct <= 85) return { msg: 'Atenção: seus gastos estão aumentando.', color: 'text-yellow-400', icon: '⚠' };
    if (pct <= 100) return { msg: 'Se continuar nesse ritmo, você pode ultrapassar seu limite.', color: 'text-orange-400', icon: '⚠' };
    return { msg: 'Você ultrapassou seu limite mensal! Hora de rever os gastos.', color: 'text-red-400', icon: '!' };
  };

  const handleToggleFixed = async (expenseId, currentStatus) => {
    if (isAnonymousMode) {
      // Modo anônimo - atualiza no localStorage
      const newStatus = !currentStatus;
      localExpensesHook.updateLocalExpense(expenseId, { is_fixed: newStatus });
      
      // Update local state
      setExpenses(prev => prev.map(exp => 
        exp.expense_id === expenseId ? { ...exp, is_fixed: newStatus } : exp
      ));
      
      toast.success(newStatus ? 'Despesa fixada para próximos meses' : 'Despesa removida das fixas');
      return;
    }
    
    // Modo autenticado
    try {
      const res = await axios.patch(`${API}/expenses/${expenseId}/toggle-fixed`, {}, { withCredentials: true });
      
      // Update local state
      setExpenses(prev => prev.map(exp => 
        exp.expense_id === expenseId ? { ...exp, is_fixed: res.data.is_fixed } : exp
      ));
      
      toast.success(res.data.is_fixed ? 'Despesa fixada para próximos meses' : 'Despesa removida das fixas');
    } catch (error) {
      console.error('Error toggling fixed:', error);
      toast.error('Erro ao atualizar despesa');
    }
  };

  const handleTogglePaid = async (expenseId) => {
    if (isAnonymousMode) {
      // Modo anônimo - atualiza no localStorage
      const expense = expenses.find(e => e.expense_id === expenseId);
      const newStatus = !expense?.is_paid;
      localExpensesHook.updateLocalExpense(expenseId, { is_paid: newStatus });
      
      // Update local state
      setExpenses(prev => prev.map(exp => 
        exp.expense_id === expenseId ? { ...exp, is_paid: newStatus } : exp
      ));
      
      toast.success(newStatus ? 'Despesa marcada como paga ✓' : 'Despesa marcada como pendente');
      return;
    }
    
    // Modo autenticado
    try {
      const res = await axios.patch(`${API}/expenses/${expenseId}/toggle-paid`, {}, { withCredentials: true });
      
      // Update local state
      setExpenses(prev => prev.map(exp => 
        exp.expense_id === expenseId ? { ...exp, is_paid: res.data.is_paid } : exp
      ));
      
      // Refresh summary so saldo / pago / a pagar update
      loadSummary();
      
      toast.success(res.data.is_paid ? 'Despesa marcada como paga ✓' : 'Despesa marcada como pendente');
    } catch (error) {
      console.error('Error toggling paid:', error);
      toast.error('Erro ao atualizar despesa');
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense({
      ...expense,
      amount: expense.amount.toString()
    });
    setShowEditExpense(true);
  };

  const handleSaveEditExpense = async () => {
    if (!editingExpense.description || !editingExpense.amount) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    setSavingExpense(true);
    
    if (isAnonymousMode) {
      // Modo anônimo - atualiza no localStorage
      localExpensesHook.updateLocalExpense(editingExpense.expense_id, {
        category_id: editingExpense.category_id,
        description: editingExpense.description,
        amount: parseFloat(editingExpense.amount),
        date: editingExpense.date,
        is_fixed: editingExpense.is_fixed,
        is_paid: editingExpense.is_paid
      });
      
      toast.success('Despesa atualizada!');
      setShowEditExpense(false);
      setEditingExpense(null);
      
      // Recarregar dados locais
      const localExpenses = localExpensesHook.getExpensesByMonth(selectedMonth);
      setExpenses(localExpenses);
      const localSummary = localExpensesHook.getSummary(selectedMonth);
      
      // Manter a meta se existir
      const savedGoal = localStorage.getItem('evite_dividas_savings_goal');
      if (savedGoal) {
        try {
          const goalData = JSON.parse(savedGoal);
          localSummary.savings_goal = goalData.monthly_goal;
          localSummary.savings_status = goalData.monthly_goal - localSummary.total;
        } catch (e) {
          console.error('Error parsing savings goal:', e);
        }
      }
      setSummary(localSummary);
      
      setSavingExpense(false);
      return;
    }
    
    // Modo autenticado
    try {
      await axios.put(`${API}/expenses/${editingExpense.expense_id}`, {
        category_id: editingExpense.category_id,
        description: editingExpense.description,
        amount: parseFloat(editingExpense.amount),
        date: editingExpense.date,
        is_fixed: editingExpense.is_fixed,
        is_paid: editingExpense.is_paid
      }, { withCredentials: true });
      
      toast.success('Despesa atualizada!');
      setShowEditExpense(false);
      setEditingExpense(null);
      loadExpenses();
      loadSummary();
    } catch (error) {
      console.error('Error updating expense:', error);
      toast.error('Erro ao atualizar despesa');
    } finally {
      setSavingExpense(false);
    }
  };

  const handleRecordActivity = async ({ silent = false } = {}) => {
    const freshSummary = isAnonymousMode ? localExpensesHook.getSummary(selectedMonth) : summary;
    const res = await gamif.recordActivity({
      total: freshSummary?.total,
      goal: freshSummary?.savings_goal,
    });
    if (res?.celebrate) {
      setCelebration(res.celebrate);
    } else if (!silent) {
      toast.success(`Dia confirmado! Você está há ${res?.ofensiva_atual || 1} dia(s) no controle 🔥`);
    }
    return res;
  };

  const handleAddExpense = async () => {
    if (!newExpense.category_id || !newExpense.description || !newExpense.amount) {
      toast.error('Preencha todos os campos');
      return;
    }
    if (newExpense.recur_type === 'parcelada') {
      const n = parseInt(newExpense.installments_total, 10);
      if (!n || n < 2) {
        toast.error('Informe o número de parcelas (mínimo 2)');
        return;
      }
    }

    // Build payload from recurrence type
    const isParcelada = newExpense.recur_type === 'parcelada';
    const isFixa = newExpense.recur_type === 'fixa';
    const payload = {
      category_id: newExpense.category_id,
      description: newExpense.description,
      amount: parseFloat(newExpense.amount),
      date: newExpense.date,
      is_fixed: isFixa,
      is_paid: false,
      installments_total: isParcelada ? parseInt(newExpense.installments_total, 10) : null
    };
    const resetExpense = { category_id: '', description: '', amount: '', date: new Date().toISOString().slice(0, 10), is_fixed: false, recur_type: 'normal', installments_total: '' };

    setSavingExpense(true);
    
    if (isAnonymousMode) {
      // Modo anônimo - salva no localStorage
      const wasEmpty = localExpensesHook.localExpenses.length === 0;
      localExpensesHook.addLocalExpense(payload);
      
      toast.success('Despesa adicionada!');
      setShowAddExpense(false);
      setNewExpense(resetExpense);
      
      // Track first expense
      if (wasEmpty) {
        trackFirstExpense();
      }
      
      // Recarregar dados locais
      setExpenses(localExpensesHook.getExpensesByMonth(selectedMonth));
      setSummary(localExpensesHook.getSummary(selectedMonth));
      
      setSavingExpense(false);
      handleRecordActivity({ silent: true });
      return;
    }
    
    // Modo autenticado - salva no backend
    try {
      await axios.post(`${API}/expenses`, payload, { withCredentials: true });
      
      toast.success('Despesa adicionada!');
      setShowAddExpense(false);
      setNewExpense(resetExpense);
      
      // Track first expense with Meta Pixel
      if (expenses.length === 0) {
        trackFirstExpense();
      }
      
      loadExpenses();
      loadSummary();
      // Only reload comparison if premium
      if (!accessInfo?.is_trial) {
        loadComparison();
      }
      handleRecordActivity({ silent: true });
    } catch (error) {
      toast.error('Erro ao adicionar despesa');
    } finally {
      setSavingExpense(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (isAnonymousMode) {
      // Modo anônimo
      localExpensesHook.deleteLocalExpense(expenseId);
      toast.success('Despesa removida');
      // Recarregar dados locais
      const localExpenses = localExpensesHook.getExpensesByMonth(selectedMonth);
      setExpenses(localExpenses);
      const localSummary = localExpensesHook.getSummary(selectedMonth);
      setSummary(localSummary);
      return;
    }
    
    // Modo autenticado
    try {
      await axios.delete(`${API}/expenses/${expenseId}`, { withCredentials: true });
      toast.success('Despesa removida');
      loadExpenses();
      loadSummary();
    } catch (error) {
      toast.error('Erro ao remover despesa');
    }
  };

  // === Income (Renda) handlers ===
  
  const handleAddIncome = async () => {
    if (!newIncome.name || !newIncome.amount) {
      toast.error('Preencha nome e valor da renda');
      return;
    }
    
    const incomeData = {
      name: newIncome.name,
      amount: parseFloat(newIncome.amount),
      income_type: newIncome.income_type,
      date: newIncome.date || new Date().toISOString().slice(0, 10)
    };
    
    if (isAnonymousMode) {
      localExpensesHook.addLocalIncome(incomeData);
      setIncomeList(localExpensesHook.getIncomeByMonth(selectedMonth));
      const localSummary = localExpensesHook.getSummary(selectedMonth);
      setSummary(localSummary);
      toast.success('Renda adicionada!');
    } else {
      try {
        await axios.post(`${API}/income`, incomeData, { withCredentials: true });
        toast.success('Renda adicionada!');
        loadIncome();
        loadSummary();
      } catch (error) {
        toast.error('Erro ao adicionar renda');
      }
    }
    
    setNewIncome({ name: '', amount: '', income_type: 'fixa', date: new Date().toISOString().slice(0, 10) });
    setShowAddIncome(false);
  };

  const handleDeleteIncome = async (incomeId) => {
    if (isAnonymousMode) {
      localExpensesHook.deleteLocalIncome(incomeId);
      setIncomeList(localExpensesHook.getIncomeByMonth(selectedMonth));
      const localSummary = localExpensesHook.getSummary(selectedMonth);
      setSummary(localSummary);
      toast.success('Renda removida');
      return;
    }
    
    try {
      await axios.delete(`${API}/income/${incomeId}`, { withCredentials: true });
      toast.success('Renda removida');
      loadIncome();
      loadSummary();
    } catch (error) {
      toast.error('Erro ao remover renda');
    }
  };



  const handleAddCategory = async () => {
    if (!newCategory.name) {
      toast.error('Digite o nome da categoria');
      return;
    }
    
    try {
      await axios.post(`${API}/expenses/categories`, newCategory, { withCredentials: true });
      toast.success('Categoria criada!');
      setShowAddCategory(false);
      setNewCategory({ name: '', color: '#00FF94', icon: 'tag' });
      loadCategories();
    } catch (error) {
      toast.error('Erro ao criar categoria');
    }
  };

  const handleSetGoal = async () => {
    if (!savingsGoal || parseFloat(savingsGoal) <= 0) {
      toast.error('Digite uma meta válida');
      return;
    }
    
    if (isAnonymousMode) {
      // Modo anônimo - salva no localStorage
      const goalValue = parseFloat(savingsGoal);
      const goalData = {
        monthly_goal: goalValue,
        month: selectedMonth
      };
      localStorage.setItem('evite_dividas_savings_goal', JSON.stringify(goalData));
      toast.success('Meta definida!');
      setShowSetGoal(false);
      // Atualizar summary local com a meta e calcular savings_status
      setSummary(prev => ({ 
        ...prev, 
        savings_goal: goalValue,
        savings_status: goalValue - (prev?.total || 0)
      }));
      return;
    }
    
    // Modo autenticado
    try {
      await axios.post(`${API}/expenses/savings-goal`, {
        monthly_goal: parseFloat(savingsGoal)
      }, { withCredentials: true });
      toast.success('Meta definida!');
      setShowSetGoal(false);
      loadSummary();
    } catch (error) {
      toast.error('Erro ao definir meta');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    // 5 meses à frente (no topo) até 11 meses atrás — permite lançar contas futuras (ex.: fatura do cartão)
    for (let i = 5; i >= -11; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      options.push({
        value: date.toISOString().slice(0, 7),
        label: date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
      });
    }
    return options;
  };

  // Data padrão ao abrir os formulários: hoje se for o mês atual, senão o dia 1 do mês selecionado
  const defaultDateForSelectedMonth = () => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    if (selectedMonth === currentMonth) return new Date().toISOString().slice(0, 10);
    return `${selectedMonth}-01`;
  };

  const openAddExpense = () => {
    setNewExpense(prev => ({ ...prev, date: defaultDateForSelectedMonth() }));
    setShowAddExpense(true);
  };

  const openAddIncome = () => {
    setNewIncome(prev => ({ ...prev, date: defaultDateForSelectedMonth() }));
    setShowAddIncome(true);
  };

  const getCategoryIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Tag;
    return IconComponent;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
      </div>
    );
  }

  // User doesn't have access to organizer (free or basic plan)
  if (!accessInfo?.has_access && user) {
    const plano = accessInfo?.plano_ativo || 'gratis';
    return (
      <div className="min-h-screen bg-[#050505]">
        <nav className="fixed top-0 left-0 right-0 z-50 glass-heavy">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Button variant="ghost" className="text-gray-400 hover:text-white ml-12 sm:ml-14" onClick={() => navigate('/')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
              </Button>
              <div className="flex items-center gap-2">
                <img src="/logo-evite-dividas.png" alt="Evite Dívidas" className="w-12 h-12 object-contain" />
                <span className="font-bold text-white">Evite Dívidas</span>
              </div>
            </div>
          </div>
        </nav>
        
        <main className="pt-24 pb-24 px-4">
          <div className="max-w-lg mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Lock className="w-16 h-16 text-amber-400 mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-white mb-3">
                {accessInfo?.trial_expired 
                  ? 'Seu período de teste expirou'
                  : 'Organizador Bloqueado'}
              </h1>
              <p className="text-gray-400 mb-2 text-sm">
                {plano === 'ia_basico'
                  ? 'Seu plano IA Básico não inclui o organizador de despesas.'
                  : 'O organizador de despesas é exclusivo do plano Premium.'}
              </p>
              <p className="text-gray-500 mb-8 text-xs">
                Controle gastos, defina metas, visualize gráficos e muito mais.
              </p>

              <div className="space-y-3 text-left mb-8">
                {/* Features locked */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <h3 className="text-sm font-bold text-white mb-3">O que o Premium inclui:</h3>
                  <div className="space-y-2">
                    {['Organizador de despesas', 'Gráficos por categoria', 'Relatórios em PDF', 'Metas financeiras', 'Comparativos mês a mês', 'IA sem limites'].map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-gray-400">
                        <Crown className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <Button 
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-black hover:opacity-90 rounded-xl font-bold px-8 py-6"
                onClick={() => navigate('/pricing')}
                data-testid="upgrade-btn"
              >
                <Crown className="w-5 h-5 mr-2" />
                Assinar Premium - R$ 29,90/mês
              </Button>
              
              <button 
                className="mt-4 text-sm text-gray-500 hover:text-gray-300 transition-colors"
                onClick={() => navigate('/')}
              >
                Voltar ao início
              </button>
            </motion.div>
          </div>
        </main>
      </div>
    );
  }

  // Variables for trial limitations
  const isTrial = accessInfo?.is_trial;
  const isPremium = !isTrial && accessInfo?.access_type === 'premium';
  const trialDaysRemaining = accessInfo?.trial_days_remaining || 0;
  const showRenewalReminder = accessInfo?.show_renewal_reminder;
  const daysUntilExpiry = accessInfo?.days_until_expiry || 0;
  const isAnonymous = accessInfo?.is_anonymous || isAnonymousMode;
  const localExpenseCount = localExpensesHook.localExpenses.length;

  // Handler para login
  const handleLoginClick = () => {
    setShowLoginPrompt(false);
    login('/expenses');
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Login Prompt Modal */}
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        expenseCount={localExpenseCount}
        onLogin={handleLoginClick}
        variant={loginPromptVariant}
      />

      {/* Anonymous Mode Banner */}
      {isAnonymous && localExpenseCount > 0 && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-b border-blue-400/30">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Cloud className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-100">
                <strong>{localExpenseCount}</strong> despesa(s) salva(s) neste dispositivo
              </span>
            </div>
            <Button 
              size="sm" 
              className="bg-blue-500 text-white hover:bg-blue-400 text-xs px-3 py-1 h-auto"
              onClick={() => {
                setLoginPromptVariant('subtle');
                setShowLoginPrompt(true);
              }}
            >
              <Shield className="w-3 h-3 mr-1" />
              Criar Conta
            </Button>
          </div>
        </div>
      )}

      {/* Trial Banner */}
      {isTrial && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-b border-amber-400/30">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-amber-100">
                Teste grátis: <strong>{trialDaysRemaining} dias</strong> restantes
              </span>
            </div>
            <Button 
              size="sm" 
              className="bg-amber-500 text-black hover:bg-amber-400 text-xs px-3 py-1 h-auto"
              onClick={() => navigate('/pricing')}
            >
              Fazer Upgrade
            </Button>
          </div>
        </div>
      )}

      {/* Premium Renewal Reminder Banner */}
      {isPremium && showRenewalReminder && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-blue-500/20 to-purple-500/20 border-b border-blue-400/30">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Crown className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-100">
                Seu plano Premium expira em <strong>{daysUntilExpiry} dias</strong>. Renove para não perder acesso!
              </span>
            </div>
            <Button 
              size="sm" 
              className="bg-blue-500 text-white hover:bg-blue-400 text-xs px-3 py-1 h-auto"
              onClick={() => navigate('/pricing')}
            >
              Renovar Agora
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <nav className={`fixed ${(isAnonymous && localExpenseCount > 0) || isTrial || (isPremium && showRenewalReminder) ? 'top-10' : 'top-0'} left-0 right-0 z-50 glass-heavy`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" className="text-gray-400 hover:text-white ml-12 sm:ml-14" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> {isAnonymous ? 'Início' : 'Dashboard'}
            </Button>
            <div className="flex items-center gap-2">
              <img src="/logo-evite-dividas.png" alt="Evite Dívidas" className="w-12 h-12 object-contain" />
              <span className="font-bold text-white hidden sm:block">Organizador de Despesas</span>
            </div>
            {gamif.status.ofensiva_atual > 0 ? (
              <div 
                className="flex items-center gap-1.5 bg-[#1A1A1A] border border-[#333] px-3 py-1.5 rounded-full text-[#FF9500] font-bold text-sm"
                data-testid="organizer-streak-badge"
                title={`${gamif.status.ofensiva_atual} ${gamif.status.ofensiva_atual === 1 ? 'dia seguido' : 'dias seguidos'} no controle!`}
              >
                <Flame className="w-4 h-4 fill-[#FF9500]" /> {gamif.status.ofensiva_atual}
              </div>
            ) : (
              <div className="w-10" />
            )}
          </div>
        </div>
      </nav>

      <main className={`${(isAnonymous && localExpenseCount > 0) || isTrial || (isPremium && showRenewalReminder) ? 'pt-32' : 'pt-24'} pb-40`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Push Notifications Banner - só para usuários logados */}
          {!isAnonymous && (
            <div className="mb-6 flex justify-end">
              <PushNotifications user={user} />
            </div>
          )}

          {/* Welcome Banner for Anonymous Users - first visit */}
          {isAnonymous && localExpenseCount === 0 && (
            <motion.div 
              className="mb-8 p-6 sm:p-8 bg-gradient-to-br from-[#00FF94]/5 via-[#0A0A0A] to-emerald-900/10 border border-[#00FF94]/20 rounded-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="max-w-xl">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 leading-tight">
                  O primeiro passo pra sair das dívidas é <span className="text-[#00FF94]">saber pra onde vai seu dinheiro.</span>
                </h3>
                <p className="text-gray-400 mb-4 text-sm sm:text-base leading-relaxed">
                  A maioria das pessoas não sabe quanto gasta por mês — e esse é o maior motivo de continuar endividado. 
                  Adicione sua primeira despesa agora. Leva menos de 30 segundos.
                </p>
                <div className="flex flex-wrap gap-3 mb-5">
                  <span className="text-xs bg-white/5 text-gray-400 px-3 py-1.5 rounded-full border border-white/10">
                    Sem cadastro
                  </span>
                  <span className="text-xs bg-white/5 text-gray-400 px-3 py-1.5 rounded-full border border-white/10">
                    Sem cartão
                  </span>
                  <span className="text-xs bg-white/5 text-gray-400 px-3 py-1.5 rounded-full border border-white/10">
                    Seus dados ficam no seu celular
                  </span>
                </div>
                <Button 
                  className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-bold px-6 py-3"
                  onClick={openAddExpense}
                  data-testid="welcome-add-expense-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Adicionar minha primeira despesa
                </Button>
              </div>
            </motion.div>
          )}

          {/* Progress Banner for Anonymous Users with expenses */}
          {isAnonymous && localExpenseCount > 0 && (
            <motion.div 
              className="mb-8 p-5 sm:p-6 bg-gradient-to-r from-[#00FF94]/5 to-emerald-900/5 border border-[#00FF94]/15 rounded-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-base font-bold text-white mb-1 flex items-center gap-2">
                    <Check className="w-5 h-5 text-[#00FF94]" />
                    Você já deu o primeiro passo!
                  </h3>
                  <p className="text-sm text-gray-400">
                    {localExpenseCount === 1 
                      ? 'Continue adicionando suas despesas para ter o panorama completo dos seus gastos.'
                      : `${localExpenseCount} despesas registradas. Crie uma conta grátis para acessar de qualquer lugar e nunca perder seus dados.`
                    }
                  </p>
                </div>
                <Button 
                  className="bg-[#00FF94]/10 text-[#00FF94] border border-[#00FF94]/30 hover:bg-[#00FF94]/20 font-bold whitespace-nowrap rounded-full"
                  onClick={() => {
                    setLoginPromptVariant('subtle');
                    setShowLoginPrompt(true);
                  }}
                  data-testid="anonymous-signup-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Criar Conta Grátis
                </Button>
              </div>
            </motion.div>
          )}

          {/* Upgrade CTA for trial users */}
          {isTrial && (
            <motion.div 
              className="mb-8 p-6 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-400/30 rounded-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Desbloqueie previsões financeiras</h3>
                    <p className="text-sm text-gray-400">
                      Faça upgrade para Premium e acesse categorias personalizadas, comparativos mensais e análises com IA baseadas nos seus gastos.
                    </p>
                  </div>
                </div>
                <Button 
                  className="bg-amber-500 text-black hover:bg-amber-400 font-bold whitespace-nowrap"
                  onClick={() => navigate('/pricing')}
                  data-testid="trial-upgrade-btn"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Fazer Upgrade
                </Button>
              </div>
            </motion.div>
          )}

          {/* Month Selector & Summary */}
          <motion.div className="mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3 flex-wrap">
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-[200px] bg-[#0A0A0A] border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {getMonthOptions().map(opt => {
                      const isCurrent = opt.value === new Date().toISOString().slice(0, 7);
                      return (
                        <SelectItem
                          key={opt.value}
                          value={opt.value}
                          className={`hover:bg-white/10 capitalize ${isCurrent ? 'text-[#00FF94] font-semibold' : 'text-white'}`}
                        >
                          {opt.label}{isCurrent ? ' • atual' : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {selectedMonth > new Date().toISOString().slice(0, 7) && (
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#00FF94]/10 border border-[#00FF94]/30 text-[#00FF94] text-xs font-semibold"
                    data-testid="future-month-badge"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Planejando mês futuro
                  </span>
                )}
              </div>
              
              <div className="flex flex-wrap gap-2">
                <Button 
                  className="bg-[#00FF94] text-black hover:bg-[#00CC76] font-semibold" 
                  onClick={openAddIncome}
                  data-testid="add-income-top-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Adicionar Renda
                </Button>
                <Button 
                  className="bg-[#00FF94] text-black hover:bg-[#00CC76] font-semibold" 
                  onClick={openAddExpense}
                  data-testid="add-expense-top-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Adicionar Despesas
                </Button>
                <Button 
                  variant="outline" 
                  className="border-[#00FF94]/40 text-[#00FF94] hover:bg-[#00FF94]/10 font-semibold" 
                  onClick={() => {
                    setSavingsGoal(summary?.savings_goal ? String(summary.savings_goal) : '');
                    setShowSetGoal(true);
                  }}
                  data-testid="add-limit-top-btn"
                >
                  <Wallet className="w-4 h-4 mr-2" />
                  {summary?.savings_goal ? 'Alterar Limite Mensal' : 'Adicionar Limite Mensal'}
                </Button>
                <Button 
                  variant="outline" 
                  className="border-[#FF9500]/40 text-[#FF9500] hover:bg-[#FF9500]/10 font-semibold" 
                  onClick={() => handleRecordActivity()}
                  data-testid="confirm-day-btn"
                >
                  <Flame className="w-4 h-4 mr-2 fill-[#FF9500]" /> Confirmar dia
                </Button>
                <Button 
                  variant="outline" 
                  className={`border-white/10 text-white ${isTrial ? 'opacity-50' : ''}`}
                  onClick={() => {
                    if (isTrial) {
                      toast.error('Criar categorias personalizadas é exclusivo do plano Premium');
                    } else {
                      setShowAddCategory(true);
                    }
                  }}
                >
                  <Settings className="w-4 h-4 mr-2" /> Categoria
                  {isTrial && <Lock className="w-3 h-3 ml-1" />}
                </Button>
              </div>
            </div>

            {/* Orçamento Monitorado - barra de progresso gamificada */}
            <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-4 sm:p-5 mb-5" data-testid="budget-monitor-bar">
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2">
                  <Flame className="w-4 h-4 text-[#FF9500] fill-[#FF9500]" />
                  <span className="text-sm font-semibold text-white">Orçamento Monitorado</span>
                  {gamif.status.ofensiva_atual > 0 && (
                    <span className="text-xs text-[#FF9500] font-medium hidden sm:inline">• {gamif.status.ofensiva_atual} {gamif.status.ofensiva_atual === 1 ? 'dia seguido' : 'dias seguidos'} no controle!</span>
                  )}
                </div>
                <span className="text-sm font-bold text-[#00FF94] font-mono" data-testid="budget-monitor-pct">{gamif.status.progress_pct}%</span>
              </div>
              <div className="h-3 bg-[#1A1A1A] rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-[#00CC76] to-[#00FF94] rounded-full"
                  initial={false}
                  animate={{ width: `${gamif.status.progress_pct}%` }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                />
              </div>
              <p className="text-[11px] text-gray-500 mt-2">
                {gamif.status.progress_pct >= 100
                  ? 'Fim do mês! Confira seu resumo e comece o próximo no controle. 🎉'
                  : `Dia ${gamif.status.dia_atual || 0} de ${gamif.status.dias_no_mes || 30} — faltam ${Math.max((gamif.status.dias_no_mes || 30) - (gamif.status.dia_atual || 0), 0)} dias para fechar o mês.`}
              </p>
            </div>

            {/* Stats Cards - Financial Overview */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {/* Dívida Total (onboarding) + progresso de quitação */}
              {debtInicial > 0 && (
                <div 
                  className="col-span-2 sm:col-span-3 bg-gradient-to-br from-[#1A0F0F] to-[#0A0A0A] border border-red-500/20 rounded-2xl p-5"
                  data-testid="organizer-debt-card"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs text-red-400/80 mb-1 flex items-center gap-1.5">
                        <TrendingDown className="w-3.5 h-3.5" /> {debtTotal <= 0 ? 'Dívida quitada' : 'Dívida restante'}
                      </p>
                      <p className="text-2xl sm:text-3xl font-bold text-white font-mono" data-testid="debt-total-value">
                        {formatCurrency(debtTotal)}
                      </p>
                      <p className="text-[11px] text-gray-500 mt-1">
                        {debtTotal <= 0
                          ? 'Parabéns! Você zerou sua dívida. 🏆'
                          : `de ${formatCurrency(debtInicial)} — vamos zerar isso juntos.`}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${debtTotal <= 0 ? 'bg-[#00FF94]/10' : 'bg-red-500/10'}`}>
                      {debtTotal <= 0
                        ? <Trophy className="w-6 h-6 text-[#00FF94]" />
                        : <TrendingDown className="w-6 h-6 text-red-400" />}
                    </div>
                  </div>

                  {/* Barra de progresso de quitação */}
                  {(() => {
                    const pago = Math.max(debtInicial - debtTotal, 0);
                    const pct = debtInicial > 0 ? Math.min(Math.round((pago / debtInicial) * 100), 100) : 0;
                    return (
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[11px] text-gray-400">{formatCurrency(pago)} quitado</span>
                          <span className="text-[11px] font-bold text-[#00FF94]" data-testid="debt-progress-pct">{pct}% pago</span>
                        </div>
                        <div className="w-full h-2.5 rounded-full bg-white/5 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-[#00FF94] to-[#33FFAD] transition-all duration-500"
                            style={{ width: `${pct}%` }}
                            data-testid="debt-progress-bar"
                          />
                        </div>
                      </div>
                    );
                  })()}

                  {/* Botão Abater dívida */}
                  {debtTotal > 0 && (
                    <button
                      onClick={() => { setPayoffAmount(''); setShowPayoffModal(true); }}
                      className="mt-4 w-full bg-[#00FF94] text-black font-bold py-2.5 rounded-full hover:bg-[#33FFAD] transition-all text-sm flex items-center justify-center gap-2"
                      data-testid="abater-divida-btn"
                    >
                      <TrendingDown className="w-4 h-4" /> Abater dívida
                    </button>
                  )}
                </div>
              )}

              {/* Receita */}
              <div className="bg-[#0A0A0A] border border-[#00FF94]/20 rounded-2xl p-4 sm:p-5" data-testid="card-receita">
                <p className="text-xs text-[#00FF94]/70 mb-1">Receita</p>
                <p className="text-xl sm:text-2xl font-bold text-[#00FF94] font-mono">
                  {formatCurrency(summary?.total_income || 0)}
                </p>
              </div>
              
              {/* Despesas Total */}
              <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-4 sm:p-5" data-testid="card-despesas">
                <p className="text-xs text-gray-500 mb-1">Despesas Total</p>
                <p className="text-xl sm:text-2xl font-bold text-white font-mono" data-testid="total-expenses">
                  {formatCurrency(summary?.total || 0)}
                </p>
              </div>
              
              {/* Saldo Disponível */}
              <div className={`bg-[#0A0A0A] border rounded-2xl p-4 sm:p-5 col-span-2 sm:col-span-1 ${
                (summary?.saldo || 0) >= 0 ? 'border-[#00FF94]/20' : 'border-red-500/30'
              }`} data-testid="card-saldo">
                <p className="text-xs text-gray-500 mb-1">Saldo Disponivel</p>
                <p className={`text-xl sm:text-2xl font-bold font-mono ${
                  (summary?.saldo || 0) >= 0 ? 'text-[#00FF94]' : 'text-red-500'
                }`}>
                  {formatCurrency(summary?.saldo || 0)}
                </p>
                <p className="text-[10px] text-gray-600 mt-0.5">Receita - Despesas pagas</p>
              </div>

              {/* Já Pago */}
              <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-4 sm:p-5" data-testid="card-pago">
                <p className="text-xs text-gray-500 mb-1">Ja Pago</p>
                <p className="text-lg font-bold text-gray-300 font-mono">
                  {formatCurrency(summary?.total_paid || 0)}
                </p>
              </div>
              
              {/* A Pagar */}
              <div className={`bg-[#0A0A0A] border rounded-2xl p-4 sm:p-5 ${
                (summary?.total_unpaid || 0) > 0 ? 'border-amber-400/20' : 'border-white/5'
              }`} data-testid="card-apagar">
                <p className="text-xs text-amber-400/70 mb-1">A Pagar</p>
                <p className="text-lg font-bold text-amber-400 font-mono">
                  {formatCurrency(summary?.total_unpaid || 0)}
                </p>
              </div>
              
              {/* Limite Mensal */}
              <div className={`bg-[#0A0A0A] border rounded-2xl p-4 sm:p-5 ${summary?.savings_status && summary.savings_status < 0 ? 'border-red-500/30' : 'border-white/5'}`} data-testid="card-limite">
                <p className="text-xs text-gray-500 mb-1">Limite Mensal</p>
                {summary?.savings_goal ? (
                  (() => {
                    const pct = Math.min(((summary.total || 0) / summary.savings_goal) * 100, 100);
                    const overBudget = (summary.total || 0) > summary.savings_goal;
                    const pctDisplay = Math.round(((summary.total || 0) / summary.savings_goal) * 100);
                    const barColor = pct <= 60 ? 'bg-[#00FF94]' : pct <= 85 ? 'bg-yellow-400' : 'bg-red-500';
                    const textColor = pct <= 60 ? 'text-[#00FF94]' : pct <= 85 ? 'text-yellow-400' : 'text-red-500';
                    return (
                      <div>
                        <p className={`text-lg font-bold font-mono ${textColor}`}>
                          {pctDisplay}%
                        </p>
                        <div className="mt-1 w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(pctDisplay, 100)}%` }} />
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <Button variant="ghost" className="text-gray-400 p-0 h-auto text-sm" onClick={() => setShowSetGoal(true)}>
                    Definir
                  </Button>
                )}
              </div>
            </div>
          </motion.div>

          {/* Feedback Inteligente */}
          {summary?.savings_goal && (() => {
            const fb = getSpendingFeedback();
            if (!fb) return null;
            return (
              <motion.div 
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className={`bg-[#0A0A0A] border border-white/5 rounded-xl px-4 py-3 flex items-center gap-3 mb-4`}
              >
                <span className={`text-lg ${fb.color}`}>{fb.icon}</span>
                <p className={`text-sm ${fb.color}`}>{fb.msg}</p>
              </motion.div>
            );
          })()}

          <Tabs defaultValue="list" value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-[#0A0A0A] border border-white/5 mb-6">
              <TabsTrigger value="rendas" className="data-[state=active]:bg-[#00FF94]/20 data-[state=active]:text-[#00FF94]" data-testid="tab-rendas">
                <TrendingUp className="w-4 h-4 mr-2" /> Rendas
              </TabsTrigger>
              <TabsTrigger value="list" className="data-[state=active]:bg-[#00FF94]/20 data-[state=active]:text-[#00FF94]" data-testid="tab-despesas">
                <Wallet className="w-4 h-4 mr-2" /> Despesas
              </TabsTrigger>
              <TabsTrigger value="metas" className="data-[state=active]:bg-[#00FF94]/20 data-[state=active]:text-[#00FF94]">
                <Flag className="w-4 h-4 mr-2" /> Metas
              </TabsTrigger>
              <TabsTrigger 
                value="chart" 
                className={`data-[state=active]:bg-[#00FF94]/20 data-[state=active]:text-[#00FF94] ${isTrial ? 'opacity-50' : ''}`}
                disabled={isTrial}
                onClick={(e) => {
                  if (isTrial) {
                    e.preventDefault();
                    toast.error('Categorias personalizadas são exclusivas do plano Premium');
                  }
                }}
              >
                <PieChart className="w-4 h-4 mr-2" /> Categorias
                {isTrial && <Lock className="w-3 h-3 ml-1" />}
              </TabsTrigger>
              <TabsTrigger 
                value="comparison" 
                className={`data-[state=active]:bg-[#00FF94]/20 data-[state=active]:text-[#00FF94] ${isTrial ? 'opacity-50' : ''}`}
                disabled={isTrial}
                onClick={(e) => {
                  if (isTrial) {
                    e.preventDefault();
                    toast.error('Comparativos mensais são exclusivos do plano Premium');
                  }
                }}
              >
                <BarChart3 className="w-4 h-4 mr-2" /> Comparativo
                {isTrial && <Lock className="w-3 h-3 ml-1" />}
              </TabsTrigger>
            </TabsList>

            {/* List Tab */}
            <TabsContent value="list">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {/* Alerts */}
                {summary?.categories?.filter(c => c.over_limit).length > 0 && (
                  <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                    <div className="flex items-center gap-2 text-red-500 mb-2">
                      <AlertTriangle className="w-5 h-5" />
                      <span className="font-semibold">Limites ultrapassados!</span>
                    </div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      {summary.categories.filter(c => c.over_limit).map(cat => (
                        <li key={cat.category_id}>
                          {cat.name}: {formatCurrency(cat.total)} (limite: {formatCurrency(cat.limit)})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {expenses.length === 0 ? (
                  <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-12 text-center">
                    <Utensils className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">Nenhuma despesa</h3>
                    <p className="text-gray-500 mb-4">Adicione sua primeira despesa do mês</p>
                    <Button className="bg-[#00FF94] text-black" onClick={openAddExpense}>
                      <Plus className="w-4 h-4 mr-2" /> Adicionar Despesa
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {expenses.map((expense) => {
                      const category = categories.find(c => c.category_id === expense.category_id || c.id === expense.category_id);
                      const IconComp = getCategoryIcon(category?.icon);
                      return (
                        <div 
                          key={expense.expense_id} 
                          className={`bg-[#0A0A0A] border rounded-xl p-4 transition-all ${
                            expense.is_paid 
                              ? 'border-[#00FF94]/50 bg-[#00FF94]/5' 
                              : expense.is_fixed 
                                ? 'border-[#00FF94]/30' 
                                : 'border-white/5'
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              {/* Checkbox de Pago */}
                              <button
                                onClick={() => handleTogglePaid(expense.expense_id)}
                                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all mt-0.5 ${
                                  expense.is_paid 
                                    ? 'bg-[#00FF94] border-[#00FF94]' 
                                    : 'border-gray-500 hover:border-[#00FF94]'
                                }`}
                              >
                                {expense.is_paid && <Check className="w-4 h-4 text-black" />}
                              </button>
                              
                              {/* Ícone da categoria */}
                              <div 
                                className="w-10 h-10 rounded-xl flex items-center justify-center relative flex-shrink-0"
                                style={{ backgroundColor: `${category?.color || '#6B7280'}20` }}
                              >
                                <IconComp className="w-5 h-5" style={{ color: category?.color || '#6B7280' }} />
                                {expense.is_fixed && (
                                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#00FF94] rounded-full flex items-center justify-center">
                                    <Pin className="w-2.5 h-2.5 text-black" />
                                  </div>
                                )}
                              </div>
                              
                              {/* Descrição - sem truncate, texto completo */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start gap-2 flex-wrap">
                                  <p className={`font-medium break-words ${expense.is_paid ? 'text-gray-400 line-through' : 'text-white'}`}>
                                    {expense.description}
                                  </p>
                                  {expense.is_fixed && (
                                    <span className="text-xs bg-[#00FF94]/20 text-[#00FF94] px-2 py-0.5 rounded-full flex-shrink-0">Fixa</span>
                                  )}
                                  {expense.installments_total && (
                                    <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full flex-shrink-0" data-testid="installment-badge">
                                      Parcela {expense.installment_number || 1}/{expense.installments_total}
                                    </span>
                                  )}
                                  {expense.is_paid && (
                                    <span className="text-xs bg-[#00FF94]/20 text-[#00FF94] px-2 py-0.5 rounded-full flex-shrink-0">Pago</span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500">{category?.name || 'Outros'} • {new Date(expense.date).toLocaleDateString('pt-BR')}</p>
                              </div>
                            </div>
                            
                            {/* Valor e Ações */}
                            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-9 sm:ml-2">
                              <span className={`text-base sm:text-lg font-bold font-mono ${expense.is_paid ? 'text-gray-500' : 'text-white'}`}>
                                {formatCurrency(expense.amount)}
                              </span>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-gray-500 hover:text-blue-400 p-1 sm:p-2"
                                onClick={() => handleEditExpense(expense)}
                                title="Editar despesa"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className={`p-1 sm:p-2 ${expense.is_fixed ? 'text-[#00FF94]' : 'text-gray-500'} hover:text-[#00FF94]`}
                                onClick={() => handleToggleFixed(expense.expense_id, expense.is_fixed)}
                                title={expense.is_fixed ? 'Remover das fixas' : 'Fixar para próximos meses'}
                              >
                                {expense.is_fixed ? <Pin className="w-4 h-4" /> : <PinOff className="w-4 h-4" />}
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-gray-500 hover:text-red-500 p-1 sm:p-2" 
                                onClick={() => handleDeleteExpense(expense.expense_id)}
                                title="Excluir despesa"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Premium Features Preview for Trial Users */}
                {isTrial && expenses.length > 0 && (
                  <motion.div 
                    className="mt-8 grid md:grid-cols-2 gap-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    {/* Preview Comparativo */}
                    <div className="relative bg-[#0A0A0A] border border-amber-500/30 rounded-2xl p-6 overflow-hidden">
                      <div className="absolute inset-0 backdrop-blur-sm bg-black/60 z-10 flex flex-col items-center justify-center p-6">
                        <Lock className="w-8 h-8 text-amber-400 mb-3" />
                        <h4 className="text-white font-bold text-center mb-2">Comparativo de 6 Meses</h4>
                        <p className="text-gray-400 text-sm text-center mb-4">Veja a evolução dos seus gastos e identifique padrões</p>
                        <Button 
                          size="sm"
                          className="bg-amber-500 text-black hover:bg-amber-400 font-bold"
                          onClick={() => navigate('/pricing')}
                        >
                          <Crown className="w-4 h-4 mr-2" />
                          Desbloquear
                        </Button>
                      </div>
                      {/* Blurred Preview Content */}
                      <div className="blur-sm">
                        <h3 className="text-lg font-semibold text-white mb-4">Últimos 6 Meses</h3>
                        <div className="flex items-end justify-between gap-2 h-32">
                          {[65, 80, 45, 90, 70, 85].map((h, i) => (
                            <div key={i} className="flex-1 bg-[#00FF94]/40 rounded-t" style={{ height: `${h}%` }} />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Preview Categorias Personalizadas */}
                    <div className="relative bg-[#0A0A0A] border border-amber-500/30 rounded-2xl p-6 overflow-hidden">
                      <div className="absolute inset-0 backdrop-blur-sm bg-black/60 z-10 flex flex-col items-center justify-center p-6">
                        <Lock className="w-8 h-8 text-amber-400 mb-3" />
                        <h4 className="text-white font-bold text-center mb-2">Categorias Personalizadas</h4>
                        <p className="text-gray-400 text-sm text-center mb-4">Crie categorias específicas para seu estilo de vida</p>
                        <Button 
                          size="sm"
                          className="bg-amber-500 text-black hover:bg-amber-400 font-bold"
                          onClick={() => navigate('/pricing')}
                        >
                          <Crown className="w-4 h-4 mr-2" />
                          Desbloquear
                        </Button>
                      </div>
                      {/* Blurred Preview Content */}
                      <div className="blur-sm">
                        <h3 className="text-lg font-semibold text-white mb-4">Minhas Categorias</h3>
                        <div className="space-y-3">
                          {['Assinaturas', 'Pets', 'Academia'].map((cat, i) => (
                            <div key={i} className="flex items-center gap-3 p-3 bg-[#121212] rounded-lg">
                              <div className="w-8 h-8 rounded-lg bg-purple-500/20" />
                              <span className="text-gray-300">{cat}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </TabsContent>

            {/* Chart Tab */}
            <TabsContent value="chart">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Pie Chart Visual */}
                  <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Distribuição por Categoria</h3>
                    {summary?.categories?.length > 0 ? (
                      <div className="relative w-48 h-48 mx-auto">
                        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                          {(() => {
                            let offset = 0;
                            return summary.categories.map((cat, i) => {
                              const percentage = cat.percentage;
                              const dashArray = `${percentage} ${100 - percentage}`;
                              const currentOffset = offset;
                              offset += percentage;
                              return (
                                <circle
                                  key={cat.category_id}
                                  cx="50" cy="50" r="40"
                                  fill="none"
                                  stroke={cat.color}
                                  strokeWidth="20"
                                  strokeDasharray={dashArray}
                                  strokeDashoffset={-currentOffset}
                                  className="transition-all duration-500"
                                />
                              );
                            });
                          })()}
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-white font-mono">{formatCurrency(summary?.total || 0)}</p>
                            <p className="text-xs text-gray-500">Total</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">Sem dados para exibir</p>
                    )}
                  </div>

                  {/* Category List */}
                  <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Por Categoria</h3>
                    <div className="space-y-4">
                      {summary?.categories?.map((cat) => {
                        const IconComp = getCategoryIcon(cat.icon);
                        return (
                          <div key={cat.category_id}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <IconComp className="w-4 h-4" style={{ color: cat.color }} />
                                <span className="text-gray-300">{cat.name}</span>
                                {cat.over_limit && <AlertTriangle className="w-4 h-4 text-red-500" />}
                              </div>
                              <span className="font-mono text-white">{formatCurrency(cat.total)}</span>
                            </div>
                            <div className="h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
                              <div 
                                className="h-full rounded-full transition-all duration-500"
                                style={{ 
                                  width: `${cat.percentage}%`, 
                                  backgroundColor: cat.over_limit ? '#EF4444' : cat.color 
                                }}
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{cat.percentage}% do total</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            </TabsContent>

            {/* Comparison Tab */}
            <TabsContent value="comparison">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-6">Últimos 6 Meses</h3>
                  {comparison?.months?.length > 0 ? (
                    <div className="flex items-end justify-between gap-4 h-64">
                      {comparison.months.map((month, i) => {
                        const maxTotal = Math.max(...comparison.months.map(m => m.total));
                        const height = maxTotal > 0 ? (month.total / maxTotal) * 100 : 0;
                        const isCurrentMonth = month.month === selectedMonth;
                        return (
                          <div key={month.month} className="flex-1 flex flex-col items-center">
                            <span className="text-sm font-mono text-white mb-2">{formatCurrency(month.total)}</span>
                            <div className="w-full bg-[#1a1a1a] rounded-t-lg overflow-hidden" style={{ height: '200px' }}>
                              <div 
                                className={`w-full rounded-t-lg transition-all duration-500 ${isCurrentMonth ? 'bg-[#00FF94]' : 'bg-[#00FF94]/40'}`}
                                style={{ height: `${height}%`, marginTop: `${100 - height}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-500 mt-2">{month.month_label}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">Sem dados suficientes para comparação</p>
                  )}
                </div>
              </motion.div>
            </TabsContent>

            {/* NOVA ABA: METAS */}
            <TabsContent value="metas">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-[#00FF94]" /> Minhas Metas
                  </h3>
                  <Button 
                    onClick={() => setShowAddGoal(true)} 
                    className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full"
                    data-testid="add-goal-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Nova Meta
                  </Button>
                </div>

                {financialGoals.length === 0 ? (
                  <div className="text-center py-12 bg-[#0A0A0A] border border-white/5 rounded-2xl">
                    <Trophy className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 mb-1">Nenhuma meta criada</p>
                    <p className="text-gray-600 text-sm mb-4">Crie objetivos financeiros para acompanhar seu progresso</p>
                    <Button 
                      variant="outline" 
                      className="border-[#00FF94]/30 text-[#00FF94] hover:bg-[#00FF94]/10"
                      onClick={() => setShowAddGoal(true)}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Criar primeira meta
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {financialGoals.map((goal) => {
                      const pct = goal.target_amount > 0 ? Math.min((goal.current_amount / goal.target_amount) * 100, 100) : 0;
                      const pctRound = Math.round(pct);
                      const isComplete = goal.current_amount >= goal.target_amount;
                      const barColor = isComplete ? 'bg-[#00FF94]' : pct >= 60 ? 'bg-blue-400' : 'bg-blue-500/60';
                      
                      return (
                        <div key={goal.goal_id} className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5 hover:border-white/10 transition-colors">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              {isComplete ? (
                                <Check className="w-5 h-5 text-[#00FF94]" />
                              ) : (
                                <Target className="w-5 h-5 text-blue-400" />
                              )}
                              <h4 className="text-white font-medium">{goal.name}</h4>
                              {isComplete && <span className="text-xs bg-[#00FF94]/20 text-[#00FF94] px-2 py-0.5 rounded-full">Concluída!</span>}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-gray-400 hover:text-white h-8 w-8 p-0"
                                onClick={() => {
                                  setEditingGoal(goal);
                                  setUpdateGoalAmount(String(goal.current_amount));
                                  setShowUpdateGoal(true);
                                }}
                                data-testid={`update-goal-${goal.goal_id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-gray-400 hover:text-red-400 h-8 w-8 p-0"
                                onClick={() => handleDeleteGoal(goal.goal_id)}
                                data-testid={`delete-goal-${goal.goal_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-gray-400">
                              {formatCurrency(goal.current_amount)} <span className="text-gray-600">de</span> {formatCurrency(goal.target_amount)}
                            </span>
                            <span className={`font-mono font-bold ${isComplete ? 'text-[#00FF94]' : 'text-blue-400'}`}>
                              {pctRound}%
                            </span>
                          </div>
                          
                          <div className="w-full h-3 bg-white/5 rounded-full overflow-hidden">
                            <motion.div 
                              className={`h-full rounded-full ${barColor}`}
                              initial={{ width: 0 }}
                              animate={{ width: `${pctRound}%` }}
                              transition={{ duration: 0.8, ease: 'easeOut' }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            </TabsContent>

            {/* NOVA ABA: RENDAS */}
            <TabsContent value="rendas">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="rendas-tab-content">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#00FF94]" /> Minhas Rendas
                  </h3>
                  <Button 
                    onClick={openAddIncome} 
                    className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full"
                    data-testid="add-income-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Nova Renda
                  </Button>
                </div>

                {/* Resumo de Renda do Mês */}
                <div className="bg-[#0A0A0A] border border-[#00FF94]/20 rounded-2xl p-5 mb-6">
                  <p className="text-xs text-[#00FF94]/70 mb-1">Total de Renda no Mês</p>
                  <p className="text-2xl sm:text-3xl font-bold text-[#00FF94] font-mono" data-testid="rendas-total">
                    {formatCurrency(summary?.total_income || 0)}
                  </p>
                </div>

                {incomeList.length === 0 ? (
                  <div className="text-center py-12 bg-[#0A0A0A] border border-white/5 rounded-2xl">
                    <Wallet className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 mb-1">Nenhuma renda registrada</p>
                    <p className="text-gray-600 text-sm mb-4">Adicione suas fontes de renda para calcular seu saldo disponível</p>
                    <Button 
                      variant="outline" 
                      className="border-[#00FF94]/30 text-[#00FF94] hover:bg-[#00FF94]/10"
                      onClick={openAddIncome}
                      data-testid="add-first-income-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Adicionar primeira renda
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {incomeList.map((income) => (
                      <div 
                        key={income.income_id} 
                        className="bg-[#0A0A0A] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors"
                        data-testid={`income-item-${income.income_id}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#00FF94]/10">
                              <TrendingUp className="w-5 h-5 text-[#00FF94]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-white break-words">{income.name}</p>
                                <span className="text-xs bg-white/10 text-gray-300 px-2 py-0.5 rounded-full flex-shrink-0 capitalize">
                                  {income.income_type === 'fixa' ? 'Fixa' : 'Variável'}
                                </span>
                              </div>
                              <p className="text-sm text-gray-500">{new Date(income.date).toLocaleDateString('pt-BR')}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="text-base sm:text-lg font-bold font-mono text-[#00FF94]">
                              {formatCurrency(income.amount)}
                            </span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-gray-500 hover:text-red-500 p-1 sm:p-2" 
                              onClick={() => handleDeleteIncome(income.income_id)}
                              title="Excluir renda"
                              data-testid={`delete-income-${income.income_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            </TabsContent>

          </Tabs>
        </div>
      </main>

      {/* Add Expense Modal */}
      <Dialog open={showAddExpense} onOpenChange={setShowAddExpense}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Nova Despesa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white mb-2 block">Categoria</Label>
              <Select value={newExpense.category_id} onValueChange={(v) => setNewExpense(p => ({ ...p, category_id: v }))}>
                <SelectTrigger className="bg-[#121212] border-white/10 text-white">
                  <SelectValue placeholder="Selecione uma categoria" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  {categories.map(cat => (
                    <SelectItem key={cat.category_id || cat.id} value={cat.category_id || cat.id} className="text-white">
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white mb-2 block">Descrição</Label>
              <Input
                value={newExpense.description}
                onChange={(e) => setNewExpense(p => ({ ...p, description: e.target.value }))}
                placeholder="Ex: Supermercado, Uber..."
                className="bg-[#121212] border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Valor</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                <Input
                  type="number"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense(p => ({ ...p, amount: e.target.value }))}
                  placeholder="0,00"
                  className="bg-[#121212] border-white/10 text-white pl-10"
                />
              </div>
            </div>
            <div>
              <Label className="text-white mb-2 block">Data</Label>
              <Input
                type="date"
                value={newExpense.date}
                onChange={(e) => setNewExpense(p => ({ ...p, date: e.target.value }))}
                className="bg-[#121212] border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Tipo de despesa</Label>
              <div className="grid grid-cols-3 gap-2" data-testid="expense-type-selector">
                {[
                  { id: 'normal', label: 'Única' },
                  { id: 'fixa', label: 'Fixa' },
                  { id: 'parcelada', label: 'Parcelada' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setNewExpense(p => ({ ...p, recur_type: opt.id }))}
                    data-testid={`expense-type-${opt.id}`}
                    className={`py-2 px-2 rounded-lg text-sm font-medium border transition-all ${
                      newExpense.recur_type === opt.id
                        ? 'bg-[#00FF94]/20 border-[#00FF94]/50 text-[#00FF94]'
                        : 'bg-[#121212] border-white/10 text-gray-400 hover:border-white/20'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {newExpense.recur_type === 'fixa' && 'Repete automaticamente todo mês (ex: aluguel). A data avança para o mês seguinte.'}
                {newExpense.recur_type === 'parcelada' && 'Repete pelo número de parcelas e some quando a última for paga.'}
                {newExpense.recur_type === 'normal' && 'Aparece apenas neste mês.'}
              </p>
            </div>
            {newExpense.recur_type === 'parcelada' && (
              <div>
                <Label className="text-white mb-2 block">Número de parcelas</Label>
                <Input
                  type="number"
                  min="2"
                  value={newExpense.installments_total}
                  onChange={(e) => setNewExpense(p => ({ ...p, installments_total: e.target.value }))}
                  placeholder="Ex: 12"
                  className="bg-[#121212] border-white/10 text-white"
                  data-testid="installments-input"
                />
                <p className="text-xs text-gray-500 mt-1">Vai aparecer como "Parcela 1/{newExpense.installments_total || 'X'}" e avançar a cada mês.</p>
              </div>
            )}
            <Button 
              className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76]" 
              onClick={handleAddExpense}
              disabled={savingExpense}
            >
              {savingExpense ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Adicionar Despesa
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Income Modal */}
      <Dialog open={showAddIncome} onOpenChange={setShowAddIncome}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#00FF94]" />
              Nova Renda
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Registre suas fontes de renda para calcular seu saldo disponível
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white mb-2 block">Nome / Descrição</Label>
              <Input
                value={newIncome.name}
                onChange={(e) => setNewIncome(p => ({ ...p, name: e.target.value }))}
                placeholder="Ex: Salário, Freelance, Aluguel..."
                className="bg-[#121212] border-white/10 text-white"
                data-testid="income-name-input"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Valor</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                <Input
                  type="number"
                  value={newIncome.amount}
                  onChange={(e) => setNewIncome(p => ({ ...p, amount: e.target.value }))}
                  placeholder="0,00"
                  className="bg-[#121212] border-white/10 text-white pl-10"
                  data-testid="income-amount-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-white mb-2 block">Tipo</Label>
              <Select value={newIncome.income_type} onValueChange={(v) => setNewIncome(p => ({ ...p, income_type: v }))}>
                <SelectTrigger className="bg-[#121212] border-white/10 text-white" data-testid="income-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="fixa" className="text-white">Fixa (recorrente)</SelectItem>
                  <SelectItem value="variavel" className="text-white">Variável (eventual)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white mb-2 block">Data</Label>
              <Input
                type="date"
                value={newIncome.date}
                onChange={(e) => setNewIncome(p => ({ ...p, date: e.target.value }))}
                className="bg-[#121212] border-white/10 text-white"
                data-testid="income-date-input"
              />
            </div>
            <Button 
              className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76]" 
              onClick={handleAddIncome}
              data-testid="save-income-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Renda
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Expense Modal */}
      <Dialog open={showEditExpense} onOpenChange={setShowEditExpense}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-[#00FF94]" />
              Editar Despesa
            </DialogTitle>
          </DialogHeader>
          {editingExpense && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-white mb-2 block">Categoria</Label>
                <Select value={editingExpense.category_id} onValueChange={(v) => setEditingExpense(p => ({ ...p, category_id: v }))}>
                  <SelectTrigger className="bg-[#121212] border-white/10 text-white">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    {categories.map(cat => (
                      <SelectItem key={cat.category_id || cat.id} value={cat.category_id || cat.id} className="text-white">
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white mb-2 block">Descrição</Label>
                <Input
                  value={editingExpense.description}
                  onChange={(e) => setEditingExpense(p => ({ ...p, description: e.target.value }))}
                  className="bg-[#121212] border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">Valor</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                  <Input
                    type="number"
                    value={editingExpense.amount}
                    onChange={(e) => setEditingExpense(p => ({ ...p, amount: e.target.value }))}
                    className="bg-[#121212] border-white/10 text-white pl-10"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white mb-2 block">Data</Label>
                <Input
                  type="date"
                  value={editingExpense.date}
                  onChange={(e) => setEditingExpense(p => ({ ...p, date: e.target.value }))}
                  className="bg-[#121212] border-white/10 text-white"
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1 flex items-center gap-3 p-3 bg-[#121212] rounded-lg border border-white/10">
                  <Checkbox 
                    id="edit_is_fixed"
                    checked={editingExpense.is_fixed}
                    onCheckedChange={(checked) => setEditingExpense(p => ({ ...p, is_fixed: checked }))}
                    className="border-white/30 data-[state=checked]:bg-[#00FF94] data-[state=checked]:border-[#00FF94]"
                  />
                  <Label htmlFor="edit_is_fixed" className="text-white cursor-pointer flex items-center gap-1">
                    <Pin className="w-4 h-4 text-[#00FF94]" />
                    Fixa
                  </Label>
                </div>
                <div className="flex-1 flex items-center gap-3 p-3 bg-[#121212] rounded-lg border border-white/10">
                  <Checkbox 
                    id="edit_is_paid"
                    checked={editingExpense.is_paid}
                    onCheckedChange={(checked) => setEditingExpense(p => ({ ...p, is_paid: checked }))}
                    className="border-white/30 data-[state=checked]:bg-[#00FF94] data-[state=checked]:border-[#00FF94]"
                  />
                  <Label htmlFor="edit_is_paid" className="text-white cursor-pointer flex items-center gap-1">
                    <Check className="w-4 h-4 text-[#00FF94]" />
                    Pago
                  </Label>
                </div>
              </div>
              <Button 
                className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76]" 
                onClick={handleSaveEditExpense}
                disabled={savingExpense}
              >
                {savingExpense ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Salvar Alterações
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Category Modal */}
      <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Nova Categoria</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white mb-2 block">Nome</Label>
              <Input
                value={newCategory.name}
                onChange={(e) => setNewCategory(p => ({ ...p, name: e.target.value }))}
                placeholder="Ex: Streaming, Pet..."
                className="bg-[#121212] border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Cor</Label>
              <div className="flex gap-2">
                {['#00FF94', '#3B82F6', '#F59E0B', '#EC4899', '#8B5CF6', '#EF4444'].map(color => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 ${newCategory.color === color ? 'border-white' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewCategory(p => ({ ...p, color }))}
                  />
                ))}
              </div>
            </div>
            <Button className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76]" onClick={handleAddCategory}>
              <Plus className="w-4 h-4 mr-2" /> Criar Categoria
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Set Spending Limit Modal */}
      <Dialog open={showSetGoal} onOpenChange={setShowSetGoal}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#00FF94]" />
              Limite de Gastos Mensais
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Defina quanto você quer gastar no máximo por mês
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white mb-2 block">Valor limite</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                <Input
                  type="number"
                  value={savingsGoal}
                  onChange={(e) => setSavingsGoal(e.target.value)}
                  placeholder="0,00"
                  className="bg-[#121212] border-white/10 text-white pl-10"
                />
              </div>
            </div>
            <Button className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76]" onClick={handleSetGoal}>
              <Wallet className="w-4 h-4 mr-2" /> Salvar Limite
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Financial Goal Modal */}
      <Dialog open={showAddGoal} onOpenChange={setShowAddGoal}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trophy className="w-5 h-5 text-[#00FF94]" />
              Nova Meta Financeira
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Defina um objetivo para acompanhar seu progresso
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white mb-2 block">Nome da meta</Label>
              <Input
                value={newGoal.name}
                onChange={(e) => setNewGoal(p => ({ ...p, name: e.target.value }))}
                placeholder="Ex: Guardar dinheiro, Quitar dívida..."
                className="bg-[#121212] border-white/10 text-white"
                data-testid="goal-name-input"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Valor da meta</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                <Input
                  type="number"
                  value={newGoal.target_amount}
                  onChange={(e) => setNewGoal(p => ({ ...p, target_amount: e.target.value }))}
                  placeholder="500,00"
                  className="bg-[#121212] border-white/10 text-white pl-10"
                  data-testid="goal-amount-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-white mb-2 block">Valor inicial (opcional)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                <Input
                  type="number"
                  value={newGoal.current_amount}
                  onChange={(e) => setNewGoal(p => ({ ...p, current_amount: e.target.value }))}
                  placeholder="0,00"
                  className="bg-[#121212] border-white/10 text-white pl-10"
                  data-testid="goal-initial-input"
                />
              </div>
            </div>
            <Button className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76]" onClick={handleCreateGoal} data-testid="save-goal-btn">
              <Trophy className="w-4 h-4 mr-2" /> Criar Meta
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Goal Progress Modal */}
      <Dialog open={showUpdateGoal} onOpenChange={setShowUpdateGoal}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-[#00FF94]" />
              Atualizar Progresso
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {editingGoal && `Meta: ${editingGoal.name} (${formatCurrency(editingGoal.target_amount)})`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white mb-2 block">Valor atual acumulado</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                <Input
                  type="number"
                  value={updateGoalAmount}
                  onChange={(e) => setUpdateGoalAmount(e.target.value)}
                  placeholder="0,00"
                  className="bg-[#121212] border-white/10 text-white pl-10"
                  data-testid="goal-update-amount"
                />
              </div>
            </div>
            <Button className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76]" onClick={handleUpdateGoalProgress} data-testid="save-goal-progress-btn">
              <Check className="w-4 h-4 mr-2" /> Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Navigation */}
      <QuickNav />

      {/* Celebration popup (pequenas vitórias) */}
      <CelebrationModal
        open={!!celebration}
        reason={celebration?.reason}
        dias={celebration?.dias}
        onClose={() => setCelebration(null)}
      />

      {/* Abater dívida modal */}
      <Dialog open={showPayoffModal} onOpenChange={setShowPayoffModal}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-[#00FF94]" />
              Abater dívida
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Quanto você pagou da sua dívida? Vamos atualizar seu progresso.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-white mb-2 block">Valor pago</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                <Input
                  type="number"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={payoffAmount}
                  onChange={(e) => setPayoffAmount(e.target.value)}
                  className="pl-10 bg-[#111] border-white/10 text-white"
                  data-testid="payoff-amount-input"
                  autoFocus
                />
              </div>
              <p className="text-[11px] text-gray-500 mt-1.5">
                Dívida restante: <span className="text-white font-mono">{formatCurrency(debtTotal)}</span>
              </p>
            </div>
            <Button
              className="w-full bg-[#00FF94] text-black hover:bg-[#33FFAD] font-bold rounded-full disabled:opacity-60"
              onClick={handlePayoffDebt}
              disabled={payingDebt || !payoffAmount || parseFloat(payoffAmount) <= 0}
              data-testid="confirm-payoff-btn"
            >
              {payingDebt ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Confirmar pagamento
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesPage;
